// ANALYSIS AND CHANGES:
// This is a new organism component that renders a position card.
// RESPONSABILIDADES:
// 1. Receive the data of a single `position` as a prop.
// 2. Receive the authentication status (`isAuthenticated`) to show or hide the admin buttons.
// 3. Receive callback functions (`onViewDetails`, `onEdit`, `onDelete`) as props and wire them to the buttons.
// 4. Render the card UI consistently.
// BENEFITS:
// - Turns the position list into a set of reusable, independent components.
// - Simplifies `CareersPage`, which now just maps the data and passes props without caring about the internal card structure.

import {
  DollarOutlined,
  EditOutlined,
  DeleteOutlined,
  EnvironmentOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import { Position } from "@common";
import { Button, Tag, Tooltip, Typography } from "antd";

const { Title } = Typography;

interface JobPositionCardProps {
  position: Position;
  isAuthenticated: boolean;
  onViewDetails: (position: Position) => void;
  onEdit: (position: Position) => void;
  onDelete: (id: string) => void;
}

export const JobPositionCard = ({
  position,
  isAuthenticated,
  onViewDetails,
  onEdit,
  onDelete,
}: JobPositionCardProps) => {
  return (
    <div className="position-card bg-rock-grey">
      {isAuthenticated && (
        <div className="action-buttons">
          <Tooltip title="Edit position">
            <Button
              icon={<EditOutlined />}
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(position);
              }}
            />
          </Tooltip>
          <Tooltip title="Remove position">
            <Button
              danger
              icon={<DeleteOutlined />}
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(position.id);
              }}
            />
          </Tooltip>
        </div>
      )}
      {position.featured && <Tag className="featured-tag">FEATURED</Tag>}

      <div className="relative p-6 pb-2 h-full flex flex-col">
        <Title level={5} className="position-title !text-white !font-azonix !uppercase mb-2">
          {position.title}
        </Title>
        <div className="flex items-center text-secondary mb-4">
          <EnvironmentOutlined className="mr-2" />
          <span>{position.location}</span>
        </div>
        <div className="flex flex-wrap gap-2 mb-4">
          <Tag color="blue">{position.department}</Tag>
          <Tag color="geekblue">{position.type}</Tag>
        </div>
        <div className="flex flex-col gap-2 mb-4 mt-auto">
          <div className="flex items-center text-primary">
            <DollarOutlined className="mr-2" />
            <span>{position.salaryRange}</span>
          </div>
          <div className="flex items-center text-secondary text-sm">
            <ClockCircleOutlined className="mr-2" />
            <span>Posted on {position.datePosted}</span>
          </div>
        </div>
      </div>
      <div className="card-footer p-4 text-center">
        <Button type="default" className="view-details-button" onClick={() => onViewDetails(position)}>
          VIEW DETAILS & APPLY
        </Button>
      </div>
    </div>
  );
};
